import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { DEFAULT_NEW_CONNECT } from './types';
import type { ConnectFormData, ConnectItem, ConnectStateValue } from './types';

const ConnectStateContext = createContext<ConnectStateValue | null>(null);

export const ConnectStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [newConnect, setNewConnectState] = useState<ConnectFormData>({ ...DEFAULT_NEW_CONNECT });
  const [currentConnect, setCurrentConnectState] = useState<ConnectItem | null>(null);
  const [step, setStep] = useState(0);
  const [modalNewConnect, setModalNewConnect] = useState(false);

  const openNewConnectModal = useCallback(() => setModalNewConnect(true), []);
  const closeNewConnectModal = useCallback(() => {
    setModalNewConnect(false);
    setNewConnectState({ ...DEFAULT_NEW_CONNECT });
    setStep(0);
  }, []);
  const setNewConnect = useCallback((data: ConnectFormData) => setNewConnectState(data), []);
  const setCurrentConnect = useCallback((connect: ConnectItem) => setCurrentConnectState(connect), []);
  const setStepExplicit = useCallback((step: number) => setStep(step), []);
  const nextStep = useCallback(() => setStep(s => s + 1), []);
  const previousStep = useCallback(() => setStep(s => (s === 0 ? 0 : s - 1)), []);

  const value = useMemo<ConnectStateValue>(
    () => ({
      newConnect,
      currentConnect,
      step,
      modalNewConnect,
      openNewConnectModal,
      closeNewConnectModal,
      setNewConnect,
      setCurrentConnect,
      setStep: setStepExplicit,
      nextStep,
      previousStep
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newConnect, currentConnect, step, modalNewConnect]
  );

  return <ConnectStateContext.Provider value={value}>{children}</ConnectStateContext.Provider>;
};

export function useConnectState(): ConnectStateValue {
  const ctx = useContext(ConnectStateContext);

  if (!ctx) {
    throw new Error('useConnectState must be used within a ConnectProvider');
  }

  return ctx;
}
